/**
 * Упаковка сырого PCM в WAV — замена `AudioEncoder.wav` из пакета `record`.
 *
 * Браузерный `MediaRecorder` не умеет WAV (только webm/opus, mp4/aac), а
 * STT ждёт именно WAV mono 16 кГц. Причина — в комментарии Dart-контроллера
 * к `RecordConfig`: «WAV mono 16 кГц — то, что ждёт STT (Bifrost): сервер
 * всё равно приводит к этому формату, а файл и загрузка в ~11 раз меньше,
 * чем при 44.1 кГц стерео по умолчанию». Поэтому запись идёт сырым Float32
 * PCM, а заголовок RIFF пишется здесь.
 *
 * Модуль чистый: из DOM используется только `Blob`, всё остальное —
 * арифметика, поэтому он покрывается юнит-тестами без браузера.
 */

/** MIME-тип готового файла — тот же, что уходит в multipart под полем "file". */
export const WAV_MIME_TYPE = 'audio/wav';

/** Канонический заголовок RIFF/WAVE с единственным чанком `data`. */
const WAV_HEADER_BYTES = 44;

/** `RIFF` и поле размера не входят в объявленный размер файла. */
const RIFF_PREFIX_BYTES = 8;

/** Размер чанка `fmt ` для несжатого PCM. */
const FMT_CHUNK_BYTES = 16;

/** Код формата: 1 — PCM без сжатия. */
const WAVE_FORMAT_PCM = 1;

/** Моно: `numChannels: 1` из `RecordConfig`. */
const NUM_CHANNELS = 1;

const BITS_PER_SAMPLE = 16;
const BYTES_PER_SAMPLE = BITS_PER_SAMPLE / 8;

/** Границы знакового 16-битного отсчёта. */
const INT16_MAX = 32767;
const INT16_MIN = -32768;

/** Смещения полей заголовка — порядок задан спецификацией RIFF/WAVE. */
const OFFSET_RIFF_ID = 0;
const OFFSET_RIFF_SIZE = 4;
const OFFSET_WAVE_ID = 8;
const OFFSET_FMT_ID = 12;
const OFFSET_FMT_SIZE = 16;
const OFFSET_AUDIO_FORMAT = 20;
const OFFSET_NUM_CHANNELS = 22;
const OFFSET_SAMPLE_RATE = 24;
const OFFSET_BYTE_RATE = 28;
const OFFSET_BLOCK_ALIGN = 32;
const OFFSET_BITS_PER_SAMPLE = 34;
const OFFSET_DATA_ID = 36;
const OFFSET_DATA_SIZE = 40;

/** Все многобайтовые поля WAV — little-endian. */
const LITTLE_ENDIAN = true;

/**
 * Собирает WAV-файл из отсчётов Float32 в диапазоне [-1, 1].
 *
 * Значения за пределами диапазона обрезаются: клиппинг лучше, чем
 * переполнение Int16, из-за которого громкий фрагмент превращается в треск.
 */
export function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  assertSampleRate(sampleRate);
  const dataBytes = samples.length * BYTES_PER_SAMPLE;
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataBytes);
  const view = new DataView(buffer);
  writeHeader(view, sampleRate, dataBytes);
  writeSamples(view, samples);
  return new Blob([buffer], { type: WAV_MIME_TYPE });
}

/**
 * Линейная интерполяция до нужной частоты дискретизации.
 *
 * Часть браузеров игнорирует запрошенные 16 кГц у `AudioContext` (Safari
 * отдаёт свои 44.1/48 кГц) — кадры тогда приходят на частоте устройства, а
 * шлюз ждёт ровно 16 кГц. Пересчёт линейный: это речь для распознавания,
 * качество ресемплера здесь не важно, важны предсказуемость и отсутствие
 * зависимостей.
 */
export function resampleLinear(
  samples: Float32Array,
  fromRate: number,
  toRate: number,
): Float32Array {
  assertSampleRate(fromRate);
  assertSampleRate(toRate);
  if (fromRate === toRate || samples.length === 0) return samples;

  const ratio = fromRate / toRate;
  const length = Math.floor(samples.length / ratio);
  const lastIndex = samples.length - 1;
  const result = new Float32Array(length);
  for (let index = 0; index < length; index += 1) {
    const position = index * ratio;
    const left = Math.floor(position);
    const right = Math.min(left + 1, lastIndex);
    const weight = position - left;
    result[index] = samples[left] * (1 - weight) + samples[right] * weight;
  }
  return result;
}

/**
 * Склейка кадров записи в один буфер.
 *
 * Кадры приходят с ворклета по 128 отсчётов; новый массив собирается
 * целиком, исходные не меняются.
 */
export function concatSamples(chunks: readonly Float32Array[]): Float32Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

/** Частота дискретизации приходит от браузера — проверяем на границе. */
function assertSampleRate(sampleRate: number): void {
  if (!Number.isFinite(sampleRate) || sampleRate <= 0) {
    throw new RangeError(
      `Недопустимая частота дискретизации: ${String(sampleRate)}`,
    );
  }
}

function writeHeader(
  view: DataView,
  sampleRate: number,
  dataBytes: number,
): void {
  const byteRate = sampleRate * NUM_CHANNELS * BYTES_PER_SAMPLE;
  const blockAlign = NUM_CHANNELS * BYTES_PER_SAMPLE;

  writeAscii(view, OFFSET_RIFF_ID, 'RIFF');
  view.setUint32(
    OFFSET_RIFF_SIZE,
    WAV_HEADER_BYTES - RIFF_PREFIX_BYTES + dataBytes,
    LITTLE_ENDIAN,
  );
  writeAscii(view, OFFSET_WAVE_ID, 'WAVE');
  writeAscii(view, OFFSET_FMT_ID, 'fmt ');
  view.setUint32(OFFSET_FMT_SIZE, FMT_CHUNK_BYTES, LITTLE_ENDIAN);
  view.setUint16(OFFSET_AUDIO_FORMAT, WAVE_FORMAT_PCM, LITTLE_ENDIAN);
  view.setUint16(OFFSET_NUM_CHANNELS, NUM_CHANNELS, LITTLE_ENDIAN);
  view.setUint32(OFFSET_SAMPLE_RATE, sampleRate, LITTLE_ENDIAN);
  view.setUint32(OFFSET_BYTE_RATE, byteRate, LITTLE_ENDIAN);
  view.setUint16(OFFSET_BLOCK_ALIGN, blockAlign, LITTLE_ENDIAN);
  view.setUint16(OFFSET_BITS_PER_SAMPLE, BITS_PER_SAMPLE, LITTLE_ENDIAN);
  writeAscii(view, OFFSET_DATA_ID, 'data');
  view.setUint32(OFFSET_DATA_SIZE, dataBytes, LITTLE_ENDIAN);
}

function writeSamples(view: DataView, samples: Float32Array): void {
  let offset = WAV_HEADER_BYTES;
  for (const sample of samples) {
    view.setInt16(offset, toInt16(sample), LITTLE_ENDIAN);
    offset += BYTES_PER_SAMPLE;
  }
}

/**
 * Float32 [-1, 1] → Int16.
 *
 * Отрицательная половина шкалы на единицу длиннее положительной, поэтому
 * множители разные — иначе −1.0 не доедет до −32768.
 */
function toInt16(sample: number): number {
  if (Number.isNaN(sample)) return 0;
  if (sample >= 1) return INT16_MAX;
  if (sample <= -1) return INT16_MIN;
  return sample < 0
    ? Math.round(sample * -INT16_MIN)
    : Math.round(sample * INT16_MAX);
}

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let index = 0; index < text.length; index += 1) {
    view.setUint8(offset + index, text.charCodeAt(index));
  }
}
